import isNil from 'lodash/isNil';

// {{name}}, with optional spaces inside the braces; captures the name
const PLACEHOLDER = /{{\s*(\w+)\s*}}/g;

const isMissing = (value: unknown): boolean => isNil(value) || value === '';

/**
 * Fills the placeholders in one `key=value` parameter, URL-encoding each value.
 * Returns `null`, so the caller drops the whole parameter, when any of its
 * values is missing: `null`, `undefined`, `''`, or a name that isn't in `values`.
 *
 * @example
 * fillParameter('v={{financeAmount}}', { financeAmount: 18500 });
 * // → 'v=18500'
 *
 * fillParameter('u={{pageUrl}}', { pageUrl: 'https://portal.save.auto/sign-up' });
 * // → 'u=https%3A%2F%2Fportal.save.auto%2Fsign-up'
 *
 * fillParameter('click={{partnerRecordUniqueIdentifier}}', {});
 * // → null
 */
const fillParameter = (
  parameter: string,
  values: Record<string, unknown>,
): string | null => {
  const names = Array.from(
    parameter.matchAll(PLACEHOLDER),
    ([, name]): string => name,
  ); // 'v={{financeAmount}}' → ['financeAmount']

  if (names.some((name): boolean => isMissing(values[name]))) {
    return null;
  }

  return parameter.replace(PLACEHOLDER, (_placeholder, name: string): string =>
    encodeURIComponent(String(values[name])),
  ); // 'v={{financeAmount}}' → 'v=18500'
};

/**
 * Fills the `{{placeholders}}` in an image or iframe pixel's URL. Only the
 * query string is touched: each parameter with a placeholder is filled or
 * dropped, and every other parameter, the path and the `#fragment` are kept
 * exactly as pasted in Contentful.
 *
 * @example
 * interpolateUrl(
 *   'https://insight.adsrvr.org/track/pxl/?ct=0:jj2ogw8&v={{financeAmount}}&click={{partnerRecordUniqueIdentifier}}',
 *   { financeAmount: 18500, partnerRecordUniqueIdentifier: null },
 * );
 * // → 'https://insight.adsrvr.org/track/pxl/?ct=0:jj2ogw8&v=18500'
 * //    ct is untouched, v is filled, click is dropped because its value is null
 */
export const interpolateUrl = (
  template: string,
  values: Record<string, unknown>,
): string => {
  const queryStart = template.indexOf('?');

  if (queryStart === -1) {
    return template;
  }

  const hashStart = template.indexOf('#', queryStart);
  const queryEnd = hashStart === -1 ? template.length : hashStart;

  // rebuilt by hand: URLSearchParams would re-encode the untouched
  // parameters, turning ct=0:jj2ogw8 into ct=0%3Ajj2ogw8
  const query = template
    .slice(queryStart + 1, queryEnd) // 'ct=0:jj2ogw8&v={{financeAmount}}&click={{partnerRecordUniqueIdentifier}}'
    .split('&') // ['ct=0:jj2ogw8', 'v={{financeAmount}}', 'click={{partnerRecordUniqueIdentifier}}']
    .map((parameter): string | null =>
      parameter.includes('{{') ? fillParameter(parameter, values) : parameter,
    ) // ['ct=0:jj2ogw8', 'v=18500', null]
    .filter((parameter): boolean => parameter !== null) // ['ct=0:jj2ogw8', 'v=18500']
    .join('&'); // 'ct=0:jj2ogw8&v=18500'

  // the part before '?', the rebuilt query (no '?' if every parameter was
  // dropped), then the #fragment if there was one
  return `${template.slice(0, queryStart)}${
    query ? `?${query}` : ''
  }${template.slice(queryEnd)}`;
};
